/**
 * Content section — typed API helpers + TanStack Query hooks.
 *
 * Backend routes live in `backend/src/routes/admin-content.ts`. The single
 * Reassign-fixture edit goes to `PATCH /v1/admin/fixtures/:id/assigned-round`
 * which lives in the existing `routes/admin.ts`.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { api } from '@/api/client';

// ---------------- shared list shape -----------------

export interface ListMeta {
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ListResponse<T> {
  data: T[];
  meta: ListMeta;
}

// ---------------- News ingestion -----------------

export interface AdminNewsItem {
  id: string;
  article_id?: string;
  kind: 'result' | 'kickoff' | 'form' | 'lock' | 'article';
  kicker: string;
  title: string;
  meta: string;
  occurred_at: string;
  stat?: { value: string; label: string };
  url?: string;
  tone?: 'red' | 'violet' | 'blue' | 'gold' | 'green';
  image?: string | null;
  source?: string;
  source_id?: string;
  source_url?: string;
  sources?: Array<{ name: string; url?: string | null; credibility?: AdminNewsCredibilityTier }>;
  source_count?: number;
  credibility?: 'verified' | 'reporting' | 'gossip' | 'community' | 'social';
  relevance?: { score: number; reason: string };
  summary?: string | null;
  brief?: string | null;
  agent_summary?: AdminNewsAgentSummary | null;
  countries?: AdminNewsCountry[];
  mentions?: AdminNewsMention[];
  entity_tags?: AdminNewsTag[];
  country_tags?: AdminNewsTag[];
  duplicate_group_id?: string | null;
  fact_group_id?: string | null;
  fact_key?: string | null;
}

export interface AdminNewsFeedResponse {
  data: AdminNewsItem[];
  meta: {
    count: number;
    cached: boolean;
    generated_at: string;
    personalized?: boolean;
    league?: string | null;
    country_iso?: string | null;
    supported_country_isos?: string[];
    refresh?: unknown;
  };
}

export interface AdminNewsFeedFilters {
  agent_id?: string;
  league?: LeagueName;
  country_iso?: string;
  limit?: number;
  refresh?: boolean;
}

export function useAdminNewsFeed(filters: AdminNewsFeedFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'content', 'news-feed', filters],
    queryFn: () => api.get<AdminNewsFeedResponse>(buildPath('/v1/news/feed', filters)),
  });
}

export interface AdminNewsSource {
  id: string;
  name: string;
  url: string;
  kind?: string;
  enabled: boolean;
  poll_interval_s: number;
  // null = global feed (visible under every league tab in the admin News
  // view); 'EPL' / 'WC' = scoped to that league only.
  league_name: 'EPL' | 'WC' | null;
  language: string;
  credibility_tier: 'verified' | 'reporting' | 'gossip' | 'community' | 'social';
  last_run_at: string | null;
  last_error: string | null;
  article_count?: number;
  run_count?: number;
  created_at: string;
  updated_at: string;
  recent_runs: Array<{
    id: string;
    started_at: string;
    finished_at: string | null;
    completed_at?: string | null;
    status: string;
    fetched_count: number;
    created_count: number;
    deduped_count: number;
    failed_count: number;
    error: string | null;
  }>;
}

export interface AdminNewsMention {
  entity_type: string;
  entity_id: string;
  label: string;
  confidence: number;
  reason: string;
  country_iso?: string | null;
}

export interface AdminNewsTag {
  label: string;
  type?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  country_iso?: string | null;
  confidence?: number | null;
  reason?: string | null;
  derived_from?: string | null;
  entity_ids?: string[];
}

export type AdminNewsCredibilityTier =
  | 'verified'
  | 'reporting'
  | 'gossip'
  | 'community'
  | 'social';

export interface AdminNewsAgentSummary {
  dek: string;
  bullets: string[];
  why_it_matters: string;
}

export interface AdminNewsCountry {
  code: string;
  name: string;
  confidence: number;
  reason: string;
}

export interface AdminNewsArticle {
  id: string;
  source_id: string;
  source_name: string;
  credibility_tier: 'verified' | 'reporting' | 'gossip' | 'community' | 'social';
  title: string;
  description: string | null;
  excerpt: string | null;
  url: string | null;
  canonical_url?: string | null;
  image_url: string | null;
  published_at: string | null;
  fetched_at?: string | null;
  status: 'visible' | 'hidden' | string;
  classification: string;
  tone: string;
  dedupe_fingerprint: string;
  duplicate_group_id?: string | null;
  duplicate_of_id?: string | null;
  fact_group_id?: string | null;
  fact_group_label?: string | null;
  duplicate_count?: number | null;
  fact_article_count?: number | null;
  fact_visible_count?: number | null;
  fact_hidden_count?: number | null;
  duplicate_match_reason?: string | null;
  duplicate_match_confidence?: number | null;
  duplicate_articles?: Array<{
    id: string;
    title: string;
    status: 'visible' | 'hidden' | string;
    source_name: string;
    credibility_tier?: string | null;
    url: string;
    published_at: string | null;
    created_at: string;
    is_current: boolean;
  }>;
  duplicate_articles_truncated?: number;
  ranking_reason?: string | null;
  relevance_reason?: string | null;
  relevance?: { score?: number | null; reason?: string | null } | null;
  summary?: string | null;
  brief?: string | null;
  agent_summary?: AdminNewsAgentSummary | null;
  countries?: AdminNewsCountry[];
  entity_tags?: AdminNewsTag[];
  country_tags?: AdminNewsTag[];
  source?: { id: string; name: string; credibility_tier: string };
  created_at: string;
  updated_at: string;
  mentions: AdminNewsMention[];
}

export interface AdminNewsStoryCopy {
  id: string;
  article_id: string;
  source_id: string;
  source_name: string;
  credibility_tier: AdminNewsCredibilityTier | string;
  title: string;
  description: string | null;
  excerpt: string | null;
  url: string | null;
  canonical_url?: string | null;
  image_url: string | null;
  published_at: string | null;
  fetched_at?: string | null;
  status: 'visible' | 'hidden' | string;
  classification: string;
  topic?: string;
  tone: string;
  dedupe_fingerprint: string;
  duplicate_match_reason?: string | null;
  duplicate_match_confidence?: number | null;
  match_key?: string | null;
  summary?: string | null;
  brief?: string | null;
  agent_summary?: AdminNewsAgentSummary | null;
  countries?: AdminNewsCountry[];
  entity_tags: AdminNewsTag[];
  country_tags: AdminNewsTag[];
  source?: { id: string; name: string; credibility_tier: string };
  created_at: string;
  updated_at: string;
  mentions: AdminNewsMention[];
  debug?: {
    match_reason?: string | null;
    match_confidence?: number | null;
    match_key?: string | null;
    dedupe_fingerprint?: string | null;
    canonical_url?: string | null;
  };
}

export interface AdminNewsStoryGroup {
  id: string;
  story_group_id: string;
  story_key: string;
  title: string;
  topic: string;
  classification: string;
  status: 'visible' | 'hidden' | string;
  copy_count: number;
  visible_count: number;
  hidden_count: number;
  summary: string | null;
  brief: string | null;
  agent_summary: AdminNewsAgentSummary | null;
  country_tags: AdminNewsTag[];
  entity_tags: AdminNewsTag[];
  copies: AdminNewsStoryCopy[];
  published_at: string | null;
  first_published_at: string | null;
  updated_at: string;
  debug?: {
    fact_key?: string | null;
    cue_key?: string | null;
    title_token_key?: string | null;
    canonical_url_key?: string | null;
    canonical_url_hash?: string | null;
    event_date?: string | null;
    stored_copy_count?: number | null;
    stored_visible_count?: number | null;
  };
}

export interface AdminNewsArticlesResponse {
  data: AdminNewsArticle[];
  meta: { count: number; total: number; limit: number; offset: number };
}

export interface AdminNewsStoryGroupsResponse {
  data: AdminNewsStoryGroup[];
  meta: { count: number; total: number; limit: number; offset: number };
}

export interface AdminNewsArticleFilters {
  q?: string;
  status?: string;
  classification?: string;
  source_id?: string;
  league?: LeagueName;
  country_iso?: string;
  limit?: number;
  offset?: number;
}

export function useAdminNewsSources(league?: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'news-sources', league ?? 'all'],
    queryFn: () =>
      api.get<{ data: AdminNewsSource[]; meta: { count: number; league: LeagueName | null } }>(
        buildPath('/v1/admin/news/sources', { league }),
      ),
  });
}

export interface NewsSourceCreatePayload {
  name: string;
  url: string;
  enabled?: boolean;
  poll_interval_s?: number;
  league_name?: 'EPL' | 'WC' | null;
  language?: string;
}

export function useCreateNewsSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NewsSourceCreatePayload) =>
      api.post<{ source: AdminNewsSource }>('/v1/admin/news/sources', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-sources'] });
    },
  });
}

export type NewsSourceUpdatePayload = Partial<
  Pick<AdminNewsSource, 'name' | 'url' | 'enabled' | 'poll_interval_s' | 'league_name' | 'language'>
>;

export function useUpdateNewsSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, patch }: { sourceId: string; patch: NewsSourceUpdatePayload }) =>
      api.patch<{ source: AdminNewsSource }>(`/v1/admin/news/sources/${sourceId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-sources'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-story-groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-articles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-feed'] });
    },
  });
}

export function useRefreshNewsSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      api.post<{
        result: {
          source_id: string;
          fetched: number;
          created: number;
          deduped: number;
          failed: number;
        };
      }>(`/v1/admin/news/sources/${sourceId}/refresh`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-sources'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-articles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-story-groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-feed'] });
    },
  });
}

export function useAdminNewsArticles(filters: AdminNewsArticleFilters) {
  return useQuery({
    queryKey: ['admin', 'content', 'news-articles', filters],
    queryFn: () =>
      api.get<AdminNewsArticlesResponse>(buildPath('/v1/admin/news/articles', filters)),
    placeholderData: keepPreviousData,
  });
}

export function useAdminNewsStoryGroups(filters: AdminNewsArticleFilters) {
  return useQuery({
    queryKey: ['admin', 'content', 'news-story-groups', filters],
    queryFn: () =>
      api.get<AdminNewsStoryGroupsResponse>(buildPath('/v1/admin/news/story-groups', filters)),
    placeholderData: keepPreviousData,
  });
}

export function useRebuildNewsStoryGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (league?: LeagueName) =>
      api.post<{ result: { league: LeagueName | null; scanned: number; assigned: number } }>(
        '/v1/admin/news/story-groups/rebuild',
        { league },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-story-groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-articles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-feed'] });
    },
  });
}

export function useAdminRankedNewsFeed(
  agentId: string,
  limit = 12,
  filters: { league?: LeagueName; countryIso?: string } = {},
) {
  const trimmedAgentId = agentId.trim();
  const normalizedAgentId =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      trimmedAgentId,
    )
      ? trimmedAgentId
      : undefined;
  const countryIso = filters.countryIso?.trim().toUpperCase();
  return useQuery({
    queryKey: ['admin', 'content', 'news-ranking-preview', agentId, limit, filters.league, countryIso],
    queryFn: () =>
      api.get<AdminNewsFeedResponse>(
        buildPath('/v1/news/feed', {
          agent_id: normalizedAgentId,
          limit,
          league: filters.league,
          country_iso: countryIso || undefined,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useUpdateNewsArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ articleId, status }: { articleId: string; status: 'visible' | 'hidden' }) =>
      api.patch<{ article: AdminNewsArticle }>(`/v1/admin/news/articles/${articleId}`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-articles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-story-groups'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'news-feed'] });
    },
  });
}

// ---------------- Scoring rules -----------------

export interface ScoringRules {
  version: string;
  minutes?: { played_any: number; played_60_plus: number };
  appearance?: { played_any: number; played_60_plus: number };
  match_result?: Record<string, number>;
  goal: Record<string, number>;
  assist: number;
  clean_sheet: Record<string, number>;
  yellow_card: number;
  red_card: number;
  saves_per_point: number;
  saves_point_value: number;
  bonus_multiplier?: number;
  own_goal: number;
  penalty_missed: number;
  penalty_saved: number;
  captain_bonus?: Record<string, number>;
  coach_bonus?: Record<string, number>;
  transfer_hit?: number;
  free_kick_bonus?: number;
}

export type LeagueName = 'EPL' | 'WC';

// World Cup rulebook (§03–§11) — structured catalog served alongside the
// scoring constants. Defined in scoring-engine/wc-rulebook.ts.
export interface WCRulebookRow {
  label: string;
  value: string;
}
export interface WCRulebookRoundRow {
  code: string;
  name: string;
  dates: string;
  teams: number;
}
export interface WCRulebookSuperpower {
  name: string;
  quota: string;
  description: string;
}
export type WCRulebookSection =
  | {
      id: string;
      heading: string;
      kind: 'rounds';
      rows: WCRulebookRoundRow[];
      footer?: string;
      span?: 'half' | 'full';
    }
  | {
      id: string;
      heading: string;
      kind: 'rows';
      rows: WCRulebookRow[];
      footer?: string;
      span?: 'half' | 'full';
    }
  | {
      id: string;
      heading: string;
      kind: 'bullets';
      bullets: string[];
      footer?: string;
      span?: 'half' | 'full';
    }
  | {
      id: string;
      heading: string;
      kind: 'superpowers';
      cards: WCRulebookSuperpower[];
      footer?: string;
      span?: 'half' | 'full';
    };
export interface WCRulebook {
  version: string;
  title: string;
  sections: WCRulebookSection[];
}

export function useScoringRules(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'scoring-rules', league],
    queryFn: () =>
      api.get<{
        data: ScoringRules;
        rulebook: WCRulebook | null;
        rulebook_v1: WCRulebook | null;
      }>(buildPath('/v1/admin/scoring-rules', { league })),
  });
}

// ---------------- Product definitions registry -----------------
//
// Glossary of user-facing product terms and derived UI concepts.
// Source of truth: `shared/product-definitions.ts`, mirrored on the backend
// at `backend/src/data/product-definitions.ts`. Admin reads via the endpoint
// below so the registry stays consistent across worker pods.

export type DefinitionScope = 'EPL' | 'WC' | 'BOTH';
export type DefinitionStatus = 'stable' | 'experimental' | 'needs_backend_support';
export type DefinitionOwner = 'product' | 'frontend' | 'backend';

export interface ProductDefinition {
  term: string;
  audience_label: string;
  short_definition: string;
  long_definition: string;
  data_sources: string[];
  calculation_notes: string;
  scope: DefinitionScope;
  status: DefinitionStatus;
  owner: DefinitionOwner;
  admin_notes?: string;
  surfaces?: string[];
}

export function useProductDefinitions(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'product-definitions', league],
    queryFn: () =>
      api.get<{
        data: ProductDefinition[];
        meta: { count: number; league_name: LeagueName };
      }>(buildPath('/v1/admin/product-definitions', { league })),
  });
}

export interface GameDataOverview {
  league_name: LeagueName;
  display_name: string;
  season: string;
  sport: string;
  provider: string;
  scope_label: string;
  /**
   * Structural round count for the league: 38 for EPL, 8 for WC. Sourced
   * from `gameConfig(gameKey).scope_codes.length` rather than the live
   * Round-row count, so the Overview tile reflects the canonical league
   * shape regardless of ingestion state.
   */
  rounds: number;
  fixtures: number;
  players: number;
  teams: number;
  coaches: number;
  latest_fixture: {
    kickoff: string;
    status: string;
    scope_code: string | null;
    round: number;
  } | null;
  mappings: string[];
}

export function useGameDataOverview(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'overview', league],
    queryFn: () =>
      api.get<{ data: GameDataOverview }>(buildPath('/v1/admin/game-data/overview', { league })),
  });
}

export interface GameDataSourceConfigEntry {
  label: string;
  value: string;
  /** True if the value is a masked secret (e.g. API key). Frontend uses
   * this to render in monospace and dim styling. */
  secret?: boolean;
}

export interface GameDataSourceRow {
  id: string;
  source: string;
  dataset: LeagueName;
  status: string;
  records: number;
  detail: string;
  /** Read-only configuration surfaced from server env vars (API keys are
   * pre-masked). Lets admins verify the running backend is wired to the
   * expected league/season IDs without exposing secrets. */
  config?: GameDataSourceConfigEntry[];
}

export function useGameDataSources(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'data-sources', league],
    queryFn: () =>
      api.get<{ data: GameDataSourceRow[]; meta: { count: number; league_name: LeagueName } }>(
        buildPath('/v1/admin/game-data/data-sources', { league }),
      ),
  });
}

// ---------------- Teams -----------------

export interface AdminTeamRow {
  team_id: string;
  name: string;
  short_name: string;
  external_id: string;
  player_count: number;
  logo_url: string | null;
}

export function useAdminTeams(q: string | undefined, league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'teams', league, q ?? ''],
    queryFn: () => api.get<ListResponse<AdminTeamRow>>(buildPath('/v1/admin/teams', { q, league })),
    placeholderData: keepPreviousData,
  });
}

// ---------------- Coaches -----------------
//
// Coaches are WC-only. EPL passes through the same hook but the backend
// returns an empty list — the UI shows an explicit empty state.

export interface AdminCoachRow {
  id: string;
  external_id: string | null;
  country_code: string;
  country_name: string | null;
  name: string;
  team_id: string | null;
  team_name: string | null;
  is_sacked: boolean;
  league_name: 'WC';
}

export interface CoachesFilters {
  q?: string;
  country?: string;
  league?: LeagueName;
}

export function useAdminCoaches(filters: CoachesFilters) {
  return useQuery({
    queryKey: ['admin', 'content', 'coaches', filters],
    queryFn: () =>
      api.get<{
        data: AdminCoachRow[];
        meta: { count: number; total: number; league_name: LeagueName };
      }>(buildPath('/v1/admin/coaches', filters)),
    placeholderData: keepPreviousData,
  });
}

// ---------------- Players -----------------

export type Position = 'GK' | 'DEF' | 'MID' | 'FWD';

export interface AdminPlayerRow {
  id: string;
  name: string;
  team_id: string;
  team_name: string;
  position: Position | string;
  cost: number;
  external_id: string;
  league_name: LeagueName;
  country_code: string | null;
  is_eliminated: boolean;
  is_active: boolean;
}

export interface PlayersFilters {
  q?: string;
  team_id?: string;
  position?: Position;
  league?: LeagueName;
  limit?: number;
  offset?: number;
}

export function useAdminPlayers(filters: PlayersFilters) {
  return useQuery({
    queryKey: ['admin', 'content', 'players', filters],
    queryFn: () => api.get<ListResponse<AdminPlayerRow>>(buildPath('/v1/admin/players', filters)),
    placeholderData: keepPreviousData,
  });
}

export interface AdminPlayerDetail extends AdminPlayerRow {
  summary: {
    games_played: number;
    goals: number;
    assists: number;
    minutes_played: number;
    total_points: number;
  };
  usage: {
    squad_count: number;
    starter_count: number;
    bench_count: number;
    captain_count: number;
    competition_count: number;
    active_competition_count: number;
    recent_squads: Array<{
      squad_id: string;
      competition_id: string;
      competition_title: string;
      competition_status: string;
      agent_id: string;
      agent_name: string;
      is_starter: boolean;
      is_captain: boolean;
      bench_order: number | null;
      created_at: string;
    }>;
  };
  recent_scores: Array<{
    fixture_id: string;
    round: number;
    effective_round: number;
    scope_code: string | null;
    league_name: LeagueName;
    home_team: string;
    away_team: string;
    is_home: boolean;
    opponent: string;
    kickoff: string;
    status: string;
    home_score: number | null;
    away_score: number | null;
    minutes_played: number;
    goals: number;
    assists: number;
    clean_sheet: boolean;
    yellow_cards: number;
    red_cards: number;
    saves: number;
    bonus: number;
    own_goals: number;
    penalties_missed: number;
    penalties_saved: number;
    free_kick_goals: number;
    goals_conceded_after_first: number;
    tackles: number;
    key_passes: number;
    shots_on_target: number;
    in_extra_time_only: boolean;
    country_eliminated: boolean;
    total_points: number;
  }>;
}

export function useAdminPlayer(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'content', 'player', id],
    queryFn: () => api.get<{ data: AdminPlayerDetail }>(`/v1/admin/players/${id}`),
    enabled: !!id,
  });
}

// ---------------- Fixtures -----------------

export interface AdminFixtureRow {
  id: string;
  external_id: string;
  round: number;
  assigned_round: number | null;
  effective_round: number;
  scope_code: string | null;
  league_name: LeagueName;
  is_replay: boolean;
  original_fixture_id: string | null;
  home_team: string;
  away_team: string;
  home_team_id: string;
  away_team_id: string;
  kickoff: string;
  home_score: number | null;
  away_score: number | null;
  status: string;
  resolved_at: string | null;
  home_difficulty?: number | null;
  away_difficulty?: number | null;
  difficulty_source?: 'fantasy_premier_league' | null;
  won_via_penalty_shootout?: boolean;
  // Knockout score breakdown — null for groups & non-PSO knockouts.
  // home_score/away_score reflect open-play (regulation + ET) only.
  regulation_home_score?: number | null;
  regulation_away_score?: number | null;
  extra_time_home_score?: number | null;
  extra_time_away_score?: number | null;
  penalty_shootout_home?: number | null;
  penalty_shootout_away?: number | null;
  market_odds?: {
    home: number;
    draw: number;
    away: number;
    source: string | null;
    fetched_at: string | null;
  } | null;
  odds_sources?: Array<{
    source: string;
    bookmaker?: string | null;
    home: number;
    draw: number;
    away: number;
    fetched_at: string;
  }>;
}

export interface FixturesFilters {
  round?: number;
  scope_code?: string;
  status?: string;
  league?: LeagueName;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useAdminFixtures(filters: FixturesFilters) {
  return useQuery({
    queryKey: ['admin', 'content', 'fixtures', filters],
    queryFn: () => api.get<ListResponse<AdminFixtureRow>>(buildPath('/v1/admin/fixtures', filters)),
    placeholderData: keepPreviousData,
  });
}

export interface FixtureRoundRow {
  round: number;
}

export function useAdminFixtureRounds(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'content', 'fixture-rounds', league],
    queryFn: () =>
      api.get<{ data: FixtureRoundRow[]; meta: { count: number; scope_codes: string[] } }>(
        buildPath('/v1/admin/fixture-rounds', { league }),
      ),
  });
}

export interface CurrentFixtureRound {
  current_round?: number | null;
  round?: number | null;
}

export function useCurrentFixtureRound() {
  return useQuery({
    queryKey: ['fixtures', 'current-round'],
    queryFn: () => api.get<CurrentFixtureRound>('/v1/fixtures/current-round'),
    staleTime: 5 * 60_000,
  });
}

// ---------------- Reassign fixture (the only edit) -----------------

export interface AssignedRoundResult {
  id: string;
  assigned_round: number | null;
  effective_round: number;
  changed: boolean;
}

export function useReassignFixtureRound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fixtureId,
      assignedRound,
      reason,
    }: {
      fixtureId: string;
      assignedRound: number | null;
      reason?: string;
    }) =>
      api.patch<AssignedRoundResult>(`/v1/admin/fixtures/${fixtureId}/assigned-round`, {
        assigned_round: assignedRound,
        reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'fixtures'] });
    },
  });
}

// ---------------- Player cost override -----------------

export interface PlayerCostUpdateResult {
  id: string;
  cost: number;
  changed: boolean;
}

export function useUpdatePlayerCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, cost }: { playerId: string; cost: number }) =>
      api.patch<PlayerCostUpdateResult>(`/v1/admin/players/${playerId}`, { cost }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'content', 'players'] });
    },
  });
}

// ---------------- helpers -----------------

function buildPath(base: string, filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '' || v === 'any') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// ---------------- Simulation -----------------

export type ScopeFixtureStatus = 'scheduled' | 'live' | 'completed';

export interface SimulationScopeRow {
  scope_code: string;
  label: string;
  scope_order: number;
  fixture_total: number;
  fixture_scheduled: number;
  fixture_live: number;
  fixture_completed: number;
  fixture_resolved: number;
  earliest_kickoff: string | null;
  latest_kickoff: string | null;
  status: ScopeFixtureStatus;
}

export interface SimulationRoundStatusResponse {
  data: SimulationScopeRow[];
  meta: { count: number; league_name: LeagueName };
}

export function useSimulationRoundStatus(league: LeagueName) {
  return useQuery({
    queryKey: ['admin', 'simulate', 'round-status', league],
    queryFn: () =>
      api.get<SimulationRoundStatusResponse>(
        buildPath('/v1/admin/simulate/round-status', { league }),
      ),
  });
}

export interface WCResetResponse {
  ok: true;
  wiped: {
    player_stats: number;
    results: number;
    rounds_reopened: number;
    competitions_reopened: number;
    fixtures_reset: number;
    placeholders_restored: number;
    players_uneliminated: number;
  };
  clock_anchor_iso: string | null;
  note: string;
}

export function useResetWCSimulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<WCResetResponse>('/v1/admin/simulate/wc-reset', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'simulate', 'round-status'] });
      qc.invalidateQueries({ queryKey: ['admin', 'content'] });
      qc.invalidateQueries({ queryKey: ['admin', 'clock'] });
    },
  });
}
